import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Median from "median-js-bridge";
import { UseUserContext } from "../Contexts/UserContext";

// backfillBanner / backfillBox fill in-house slots that have no in-house ad to
// show (see Ad.js). They reuse the leaderboard / rectangle units until Armanet
// issues dedicated ones; swap the ids here when it does, so backfill revenue
// reports separately and a page never carries the same unit twice. Sharing an id
// costs fill, not just reporting: armanet-pxl.js keys its request by unit id, so
// two elements with the same id that come into view within the same 100ms get
// one ad between them, and the other is never asked for again.
const WEB_AD_IDS = {
  leaderboard: "14ee1cf8-48f0-418d-972c-624db53a7cce",
  leaderboard2: "7327cc3c-1bf7-4cac-98e8-430022e3d214",
  rectangle: "14d5ee91-c729-42b4-b62c-b7b5c12296ad",
  backfillBanner: "14ee1cf8-48f0-418d-972c-624db53a7cce",
  backfillBox: "14d5ee91-c729-42b4-b62c-b7b5c12296ad",
};

// Only the Median (formerly GoNative) app gets these. Mobile web browsers (Safari,
// Chrome, etc.) use the web units above, whatever the screen width.
const APP_AD_IDS = {
  leaderboard: "056dc56e-3ffc-43e3-92df-6a3a21c5354a",
  leaderboard2: "0572e218-a291-4379-ac45-9817c9c4c477",
  rectangle: "9e11e48c-defc-406a-a547-e97dccd4a35a",
  backfillBanner: "056dc56e-3ffc-43e3-92df-6a3a21c5354a",
  backfillBox: "9e11e48c-defc-406a-a547-e97dccd4a35a",
};

// armanet-pxl.js asks for an ad once per `data-armanet` element: it tags each
// element it finds with the unit id, requests an ad when it comes into view,
// and never asks for that element again. It has no refresh call. (The
// `window.__armanet_pxl()` this component used to call does not exist in the
// script, so every refresh was a silent no-op and each unit got one ad for its
// lifetime.) Refreshing therefore means mounting a fresh element, which is what
// `loadKey` does, then telling the script to look for it now rather than on its
// next once-a-second scan.
//
// The `data-armanet` element is created by hand so that React never owns it.
// armanet-pxl.js removes the element itself when an interstitial's countdown ends
// or a sticky ad is closed, and the creatives it injects run arbitrary scripts. If
// React owned the element, its next refresh would call removeChild on a node that
// is already gone, and that error takes down the whole site, not just this slot.
//
// `autoRefresh={false}` hands the refresh cadence to the parent. Ad.js does this
// for backfilled slots and remounts the unit with a new `key` for each in-house
// load that comes back empty.
function ArmanetAd({ type, autoRefresh = true }) {
  const { user } = UseUserContext();
  const containerRef = useRef(null);
  const adIds = Median.isNativeApp() ? APP_AD_IDS : WEB_AD_IDS;
  const [inView, setInView] = useState(false);
  // The element mounted with the component is its first load.
  const [totalAdLoads, setTotalAdLoads] = useState(1);
  const [loadKey, setLoadKey] = useState(0);
  const mountedLocationRef = useRef(true);
  const location = useLocation();

  function refreshAd() {
    if (
      user?.isPremium ||
      !containerRef.current ||
      document.fullscreenElement != null
    )
      return;
    setLoadKey((prev) => prev + 1);
    setTotalAdLoads((prev) => prev + 1);
  }

  // Mounts a fresh element for each load, including the first. remove() is safe
  // if Armanet has already taken the element out of the DOM.
  const unitId = adIds[type];
  const isPremium = user?.isPremium;
  useEffect(() => {
    if (!containerRef.current) return;
    const element = document.createElement("div");
    element.setAttribute("data-armanet", unitId);
    containerRef.current.appendChild(element);
    window.dispatchEvent(new Event("loadAds"));
    return () => element.remove();
  }, [loadKey, unitId, isPremium]);

  // Refresh on every navigation, resetting the load count. Keyed on the
  // router's location rather than on patched history methods: each instance
  // used to wrap pushState/replaceState on mount and restore its own snapshot on
  // unmount, so with instances mounting and unmounting independently (backfilled
  // in-house slots among them) one unmount could restore a stale function and
  // leave the other instances deaf to navigation. Mounting is not a navigation:
  // the first element is already a fresh load.
  useEffect(() => {
    if (mountedLocationRef.current) {
      mountedLocationRef.current = false;
      return;
    }
    if (!autoRefresh) return;
    setTotalAdLoads(0);
    refreshAd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // 60-second refresh interval (matches Scout ad timing)
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      if (inView && totalAdLoads <= 5) {
        refreshAd();
      }
    }, 60000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalAdLoads, inView, autoRefresh]);

  // Track viewport visibility
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setInView(entry.isIntersecting);
        });
      },
      { threshold: 0.1 },
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [containerRef]);

  if (isPremium) return null;

  return <div className={`armanetAd armanetAd--${type}`} ref={containerRef} />;
}

export default ArmanetAd;
